import './DashboardEditor.scss';

import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Button from 'src/component/UI/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import DashboardEditorRow from './DashboardEditorRow';
import WidgetPalette from './WidgetPalette';
import { assignEditorIds } from './utils';
import { WidgetRegistryEntry } from './widgetRegistry';
import DashboardStatistics from 'src/component/UI/Statistics/DashboardStatistics';
import { useTranslation } from 'src/i18n';

interface DragInfo {
  draggedIndex: number;
  previewIndex: number;
  offsetX: number;
  offsetY: number;
  cloneWidth: number;
  cloneHeight: number;
  initialX: number;
  initialY: number;
}

interface DashboardEditorProps {
  layout: any[];
  onLayoutChange: (layout: any[]) => void;
  onSave: () => void;
  onCancel: () => void;
  onReset: () => void;
}

export default function DashboardEditor ({ layout, onLayoutChange, onSave, onCancel, onReset }: DashboardEditorProps) {
  const { t } = useTranslation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [dragInfo, setDragInfo] = useState<DragInfo | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const cloneRef = useRef<HTMLDivElement>(null);
  const dragTrackRef = useRef<{ previewIndex: number } | null>(null);
  const prevPositionsRef = useRef<Record<string, DOMRect> | null>(null);
  const isAnimatingRef = useRef(false);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const onLayoutChangeRef = useRef(onLayoutChange);
  onLayoutChangeRef.current = onLayoutChange;
  const dragInfoRef = useRef(dragInfo);
  dragInfoRef.current = dragInfo;

  const rows = layout[0]?.children || [];

  const displayItems = useMemo(() => {
    if (!dragInfo) {
      return rows.map((row: any) => ({ row, isPlaceholder: false }));
    }

    const { draggedIndex, previewIndex } = dragInfo;
    const draggedRow = rows[draggedIndex];
    const filtered = rows.filter((_: any, i: number) => i !== draggedIndex);
    const result = filtered.map((row: any) => ({ row, isPlaceholder: false }));
    result.splice(previewIndex, 0, { row: draggedRow, isPlaceholder: true });

    return result;
  }, [rows, dragInfo]);

  const capturePositions = useCallback(() => {
    if (!containerRef.current) return;
    const positions: Record<string, DOMRect> = {};
    containerRef.current.querySelectorAll<HTMLElement>('[data-editor-id]').forEach((el) => {
      const id = el.dataset.editorId;
      if (id) positions[id] = el.getBoundingClientRect();
    });
    prevPositionsRef.current = positions;
  }, []);

  // Pixels on each side of the midpoint that form a "dead zone". Inside this band
  // we keep the current preview arrangement rather than flipping, which eliminates
  // the rapid back-and-forth jitter when hovering near the centre of a card.
  const DROP_HYSTERESIS = 60;

  const getDropIndex = useCallback((cursorX: number, cursorY: number, currentPreviewIndex: number): number => {
    if (!containerRef.current) return 0;

    const containerWidth = containerRef.current.getBoundingClientRect().width;
    const items = Array.from(containerRef.current.querySelectorAll<HTMLElement>('[data-drop-target]'));

    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();

      if (cursorY < rect.top) {
        return i;
      }

      if (cursorY >= rect.top && cursorY <= rect.bottom) {
        if (cursorX >= rect.left && cursorX <= rect.right) {
          // Full-width items are stacked vertically: split on the vertical midpoint.
          // Partial-width items sit side by side in the same row: split on the horizontal midpoint.
          const isFullWidth = rect.width > containerWidth * 0.8;

          if (isFullWidth) {
            const midY = rect.top + rect.height / 2;
            if (cursorY < midY - DROP_HYSTERESIS) return i;
            if (cursorY > midY + DROP_HYSTERESIS) return i + 1;
          } else {
            const midX = rect.left + rect.width / 2;
            if (cursorX < midX - DROP_HYSTERESIS) return i;
            if (cursorX > midX + DROP_HYSTERESIS) return i + 1;
          }
          // Inside the dead zone: stay committed to whichever side we're already on
          return currentPreviewIndex <= i ? i : i + 1;
        }
      }
    }

    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      if (cursorY >= rect.top && cursorY <= rect.bottom && cursorX < rect.left) {
        return i;
      }
    }

    for (let i = items.length - 1; i >= 0; i--) {
      const rect = items[i].getBoundingClientRect();
      if (cursorY >= rect.top && cursorY <= rect.bottom && cursorX > rect.right) {
        return i + 1;
      }
    }

    return items.length;
  }, []);

  const handleDragStart = useCallback((index: number, e: React.PointerEvent) => {
    const rowEl = (e.target as HTMLElement).closest('.editor-row');
    if (!rowEl) return;

    const rect = rowEl.getBoundingClientRect();

    setDragInfo({
      draggedIndex: index,
      previewIndex: index,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      cloneWidth: rect.width,
      cloneHeight: rect.height,
      initialX: rect.left,
      initialY: rect.top,
    });
    dragTrackRef.current = { previewIndex: index };
    document.body.classList.add('is-dragging-dashboard');
  }, []);

  const isDragging = dragInfo !== null;

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: PointerEvent) => {
      const info = dragInfoRef.current;
      if (!info) return;

      // Clone position always updates immediately for smooth visual feedback
      if (cloneRef.current) {
        cloneRef.current.style.transform = `translate(${e.clientX - info.offsetX}px, ${e.clientY - info.offsetY}px)`;
      }

      // Throttle the layout decision to one per animation frame.
      // pointermove can fire at 1000Hz; computing drop index that often races
      // the FLIP animation and causes jitter while moving.
      const clientX = e.clientX;
      const clientY = e.clientY;

      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          if (isAnimatingRef.current) return;

          const currentInfo = dragInfoRef.current;
          if (!currentInfo) return;

          const newIndex = getDropIndex(clientX, clientY, dragTrackRef.current?.previewIndex ?? currentInfo.draggedIndex);

          if (dragTrackRef.current && newIndex !== dragTrackRef.current.previewIndex) {
            capturePositions();
            dragTrackRef.current.previewIndex = newIndex;
            setDragInfo((prev) => (prev ? { ...prev, previewIndex: newIndex } : null));
          }
        });
      }
    };

    const handleUp = () => {
      const info = dragInfoRef.current;
      if (!info) return;

      const previewIndex = dragTrackRef.current?.previewIndex ?? info.draggedIndex;

      if (previewIndex !== info.draggedIndex) {
        const currentLayout = layoutRef.current;
        const newLayout = JSON.parse(JSON.stringify(currentLayout));
        const children = newLayout[0].children;
        const [moved] = children.splice(info.draggedIndex, 1);
        children.splice(previewIndex, 0, moved);
        onLayoutChangeRef.current(newLayout);
      }

      setDragInfo(null);
      dragTrackRef.current = null;
      isAnimatingRef.current = false;
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      document.body.classList.remove('is-dragging-dashboard');
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDragInfo(null);
        dragTrackRef.current = null;
        isAnimatingRef.current = false;
        if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
        if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        document.body.classList.remove('is-dragging-dashboard');
      }
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDragging, getDropIndex, capturePositions]);

  // FLIP animation for smooth item repositioning during drag
  useLayoutEffect(() => {
    const prev = prevPositionsRef.current;
    if (!prev || !containerRef.current) return;
    prevPositionsRef.current = null;

    let anyAnimated = false;

    containerRef.current.querySelectorAll<HTMLElement>('[data-editor-id]').forEach((el) => {
      const id = el.dataset.editorId;
      if (!id || !prev[id]) return;

      const oldRect = prev[id];
      const newRect = el.getBoundingClientRect();
      const dx = oldRect.left - newRect.left;
      const dy = oldRect.top - newRect.top;

      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

      anyAnimated = true;
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.style.transition = 'none';

      // Force reflow so the browser registers the starting position
      el.getBoundingClientRect();

      el.style.transition = 'transform 200ms ease';
      el.style.transform = '';
    });

    // While items are mid-animation getBoundingClientRect() returns transitioning
    // positions, which makes getDropIndex unstable. Lock it out for the duration.
    if (anyAnimated) {
      isAnimatingRef.current = true;
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      animationTimerRef.current = setTimeout(() => {
        isAnimatingRef.current = false;
      }, 80);
    }
  }, [dragInfo?.previewIndex]);

  const handleRemove = (index: number) => {
    const newLayout = JSON.parse(JSON.stringify(layout));
    newLayout[0].children.splice(index, 1);
    onLayoutChange(newLayout);
  };

  const handleAdd = (entry: WidgetRegistryEntry) => {
    const newLayout = JSON.parse(JSON.stringify(layout));
    const newRow = assignEditorIds([{ type: 'grid', container: true, spacing: 3, children: [JSON.parse(JSON.stringify(entry.defaultLayout))] }]);
    newLayout[0].children.push(newRow[0].children[0]);
    onLayoutChange(newLayout);
    setPaletteOpen(false);
  };

  const handleResetConfirm = () => {
    setConfirmReset(false);
    onReset();
  };

  const draggedRow = dragInfo ? rows[dragInfo.draggedIndex] : null;

  return (
    <div className='dashboard-editor'>
      <div className='dashboard-editor-toolbar'>
        <Button cta onClick={() => setPaletteOpen(true)}>
          {t('dashboard.addCard')}
        </Button>
        <Button cta onClick={() => setConfirmReset(true)}>
          {t('dashboard.resetToDefault')}
        </Button>
        <div className='dashboard-editor-toolbar-spacer' />
        <Button background={false} onClick={onCancel}>
          {t('dashboard.cancelEdit')}
        </Button>
        <Button cta onClick={onSave}>
          {t('dashboard.saveDashboard')}
        </Button>
      </div>

      <div ref={containerRef} className='dashboard-editor-rows'>
        {displayItems.map(({ row, isPlaceholder }) => {
          if (isPlaceholder) {
            return (
              <div
                key={`placeholder-${row._editorId}`}
                className='editor-row drag-placeholder'
                data-drop-target
                style={{
                  gridColumn: `span ${row.md || 12}`,
                  height: dragInfo?.cloneHeight,
                }}
              />
            );
          }

          return (
            <DashboardEditorRow
              key={row._editorId}
              row={row}
              index={rows.indexOf(row)}
              onRemove={handleRemove}
              onDragStart={handleDragStart}
              md={row.md || 12}
            >
              <DashboardStatistics layoutPreferencePath={null} defaultLayoutOverride={[{ type: 'grid', container: true, spacing: 0, children: [{ ...row, md: 12, xs: 12 }] }]} />
            </DashboardEditorRow>
          );
        })}
      </div>

      {dragInfo && draggedRow && createPortal(
        <div
          ref={cloneRef}
          className='dashboard-editor-drag-clone'
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: dragInfo.cloneWidth,
            transform: `translate(${dragInfo.initialX}px, ${dragInfo.initialY}px)`,
            pointerEvents: 'none',
            zIndex: 10000,
          }}
        >
          <div className='editor-row dragging'>
            <div className='editor-row-content'>
              <DashboardStatistics layoutPreferencePath={null} defaultLayoutOverride={[{ type: 'grid', container: true, spacing: 0, children: [{ ...draggedRow, md: 12, xs: 12 }] }]} />
            </div>
          </div>
        </div>,
        document.body
      )}

      <WidgetPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onAdd={handleAdd}
        currentRows={rows}
      />

      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)}>
        <DialogTitle>{t('dashboard.resetToDefault')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('dashboard.confirmReset')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button background={false} onClick={() => setConfirmReset(false)}>{t('dashboard.cancelEdit')}</Button>
          <Button cta onClick={handleResetConfirm}>{t('dashboard.resetToDefault')}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
